<template>
  <div
    class="add-card-modal-overlay"
    role="dialog"
    aria-modal="true"
    :aria-labelledby="`${uid}-title`"
    @click.self="onCloseRequested"
  >
    <div class="add-card-modal" :class="{ 'is-narrow': isNarrowViewport }">

      <header class="modal-header">
        <h2 :id="`${uid}-title`" class="modal-title">
          Add a card to <span class="section-name">"{{ sectionDisplayName }}"</span> section
        </h2>
        <button
          ref="closeBtn"
          type="button"
          class="close-btn"
          title="Close"
          aria-label="Close"
          @click="onCloseRequested"
        >×</button>
      </header>

      <nav class="tab-strip" role="tablist" aria-label="Card source">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          role="tab"
          :aria-selected="String(activeTab === tab.key)"
          :class="['tab-btn', { 'is-active': activeTab === tab.key }]"
          @click="activeTab = tab.key"
        >
          <span class="tab-label">{{ tab.label }}</span>
          <span class="tab-count">({{ tab.count }})</span>
        </button>
      </nav>

      <div class="modal-body">

        <!-- Questions tab: embedded picker -->
        <div v-show="activeTab === 'questions'" class="tab-panel">
          <QuestionBankBrowser
            mode="picker"
            :exclude-question-ids="excludeQuestionIds"
            @select-question="onSelectQuestion"
          />
        </div>

        <!-- Flow cards: fixed built-in catalogue (no search/library). -->
        <div v-if="activeTab === 'flow'" class="tab-panel">
          <p v-if="flowError" class="flow-message flow-error">{{ flowError }}</p>
          <p v-else-if="flowLoading && !flowCards.length" class="flow-message">Loading…</p>
          <p v-else-if="!flowCards.length" class="flow-message">No flow cards available.</p>
          <ul v-else class="flow-list">
            <li v-for="fc in flowCards" :key="fc.id">
              <button type="button" class="flow-row" @click="onSelectFlowCard(fc)">
                <span class="flow-row-main">
                  <span class="flow-row-name">{{ flowCardName(fc) }}</span>
                  <span class="flow-row-code mono">{{ fc.code }}</span>
                </span>
                <span class="flow-row-add" aria-hidden="true">+ Add</span>
              </button>
            </li>
          </ul>
        </div>

        <!-- Content cards tab: embedded picker -->
        <div v-show="activeTab === 'content'" class="tab-panel">
          <ContentCardBrowser
            :exclude-card-ids="excludeCardIds"
            @select-card="onSelectContentCard"
          />
        </div>

      </div>
    </div>

    <!-- Step 1 selection toast. Lives inside the overlay so it overlays
         the modal too. Step 2 will replace this with actual add behaviour. -->
    <transition name="toast-fade">
      <div
        v-if="toast"
        class="toast"
        :class="`toast-${toast.kind}`"
        role="status"
        aria-live="polite"
      >{{ toast.text }}</div>
    </transition>
  </div>
</template>

<script>
import QuestionBankBrowser from '@/components/admin/QuestionBankBrowser.vue';
import ContentCardBrowser, { titleWithFallback } from '@/components/admin/ContentCardBrowser.vue';
import apiService from '@/services/apiService';
import i18n from '@/i18n';
import {
  questionsState,
} from '@/components/admin/_data/questions.js';
import {
  contentCardsLibraryState,
  loadContentCardsLibrary,
} from '@/components/admin/_data/contentCardsLibrary.js';

let uidCounter = 0;

export default {
  name: 'AddCardModal',
  components: { QuestionBankBrowser, ContentCardBrowser },
  props: {
    questionnaireSlug: { type: String, required: true },
    sectionId: { type: Number, required: true },
    sectionName: { type: String, default: '' },
    // Junction Step 3: informational signal. Each chooser tab marks
    // matching rows with an "Already in this section" badge but keeps
    // them clickable — duplicates are allowed.
    excludeQuestionIds: { type: Array, default: () => [] },
    excludeCardIds: { type: Array, default: () => [] },
  },
  emits: ['close', 'select-question', 'select-card', 'select-flow'],
  data() {
    uidCounter += 1;
    return {
      uid: `addcard-${uidCounter}`,
      activeTab: 'questions',
      isNarrowViewport: false,
      // Flow-card catalogue, loaded on mount. Fixed handful of built-in
      // rows; no search or library treatment.
      flowCards: [],
      flowLoading: false,
      flowError: null,
      // The tab counts are a snapshot at open time (per design: search
      // input shouldn't churn the count). We capture them in mounted()
      // after data has had a chance to land via the embedded browser's
      // own fetch. If data lands later, the computed re-reads the store —
      // we accept the small open-time→data-load update as friendly.
      toast: null,
      toastTimer: null,
      _onKeydown: null,
      _onResize: null,
    };
  },
  computed: {
    sectionDisplayName() {
      return (this.sectionName || '').trim() || `Section ${this.sectionId}`;
    },
    activeQuestionCount() {
      // Reads through the reactive store. Picker mode hides archived rows
      // so we only count active ones. Search input does NOT enter this
      // formula — the count stays stable as the user types.
      return questionsState.items.filter(q => q.status === 'active').length;
    },
    activeContentCardCount() {
      // Reads through the shared contentCardsLibrary store. The store
      // is loaded on modal mount (see mounted hook); this computed
      // re-runs reactively when the fetch lands, so the badge updates
      // from "0" to the real count automatically. Legacy 'APPROVED'
      // status is treated as active during the lowercase migration.
      return contentCardsLibraryState.items.filter(
        c => c.status === 'active' || c.status === 'APPROVED',
      ).length;
    },
    tabs() {
      return [
        {
          key: 'questions',
          label: 'Questions',
          count: this.activeQuestionCount,
        },
        {
          key: 'flow',
          label: 'Flow cards',
          // The built-in catalogue, loaded on mount. 0 until the fetch
          // lands, then the real count.
          count: this.flowCards.length,
        },
        {
          key: 'content',
          label: 'Content cards',
          count: this.activeContentCardCount,
        },
      ];
    },
  },
  mounted() {
    // Kick off the content-cards library load so the tab count
    // populates even before the user clicks the Content tab. The store
    // de-dupes concurrent calls, so when the user opens that tab the
    // browser's own loadContentCardsLibrary() just rides this promise.
    // Forced, because the store otherwise holds its first fetch for the
    // whole session: a card created or approved in the Card Editor after
    // that would stay invisible here until a full page reload.
    loadContentCardsLibrary({ force: true }).catch(() => {
      // Errors surface on the Content tab (loadError computed). Quiet
      // here so a transient failure doesn't crash the modal open.
    });

    // Load the flow-card catalogue so the Flow tab and its count populate.
    this.loadFlowCards();

    // Esc closes the modal. document-level listener because focus may
    // be anywhere inside the modal subtree.
    this._onKeydown = (e) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        this.onCloseRequested();
      }
    };
    document.addEventListener('keydown', this._onKeydown);

    // Below 800 px we degrade to a fullscreen modal (spec point 7).
    this._onResize = () => {
      this.isNarrowViewport = window.innerWidth < 800;
    };
    this._onResize();
    window.addEventListener('resize', this._onResize);

    // Land focus on something inside the modal so keyboard users aren't
    // stuck behind. The close button is the least disruptive — it doesn't
    // hijack the search input's caret.
    this.$nextTick(() => {
      if (this.$refs.closeBtn && typeof this.$refs.closeBtn.focus === 'function') {
        this.$refs.closeBtn.focus();
      }
    });
  },
  beforeUnmount() {
    if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown);
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
  methods: {
    onCloseRequested() {
      this.$emit('close');
    },
    async loadFlowCards() {
      this.flowLoading = true;
      this.flowError = null;
      try {
        this.flowCards = await apiService.fetchFlowCards();
      } catch (e) {
        this.flowError = 'Could not load flow cards. Please try again.';
      } finally {
        this.flowLoading = false;
      }
    },
    // Resolve a flow card's i18n name to the active locale, falling back
    // to English, then any non-empty value, then the code.
    flowCardName(card) {
      const n = card && card.name;
      if (!n || typeof n !== 'object') return (card && card.code) || '';
      const loc = i18n.global.locale.value;
      return n[loc] || n.en
        || Object.values(n).find(v => typeof v === 'string' && v.trim())
        || card.code || '';
    },
    onSelectFlowCard(card) {
      // The parent (QuestionnaireSectionList) persists via
      // addItemToQuestionnaire and shows success/error.
      this.$emit('select-flow', card);
    },
    onSelectQuestion(question) {
      // Step 1 behaviour: log + toast, no DB write, modal stays open so
      // Tamara can test selecting several questions in a row before we
      // wire the real plumbing in Step 2.
      // eslint-disable-next-line no-console
      console.log('[AddCardModal Step 1] Selected question:', question);
      this.showToast(
        'info',
        `Selected: ${question.code} — wiring still in progress.`,
      );
      this.$emit('select-question', question);
    },
    onSelectContentCard(card, activeLocale) {
      // Same Step 1 shape as the question handler: log + toast, no DB
      // write, modal stays open. The title comes via the locale-fallback
      // helper (active → en → first non-empty → id) so the toast is
      // useful even when the card is only partially translated.
      // eslint-disable-next-line no-console
      console.log('[AddCardModal Step 1] Selected content card:', card);
      const title = titleWithFallback(card, activeLocale) || `#${card.id}`;
      this.showToast(
        'info',
        `Selected: ${title} — wiring still in progress.`,
      );
      this.$emit('select-card', card);
    },
    showToast(kind, text) {
      if (this.toastTimer) clearTimeout(this.toastTimer);
      this.toast = { kind, text };
      const ms = kind === 'error' ? 6000 : 3500;
      this.toastTimer = setTimeout(() => {
        this.toast = null;
        this.toastTimer = null;
      }, ms);
    },
  },
};
</script>

<style scoped>
.add-card-modal-overlay {
  --color-bg-card: #ffffff;
  --color-bg-muted: #f5f4ef;
  --color-bg-soft: #fafaf7;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.12);
  --color-border-strong: rgba(0, 0, 0, 0.2);
  --color-accent: #534AB7;
  --color-accent-soft: rgba(83, 74, 183, 0.12);
  --color-accent-dark: #3F368F;
  --color-bad-dark: #A92F26;
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: 'Montserrat', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;

  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4vh 2vw;
  z-index: 2500;
  font-family: var(--font-sans);
  box-sizing: border-box;
}

.add-card-modal-overlay *,
.add-card-modal-overlay *::before,
.add-card-modal-overlay *::after {
  box-sizing: border-box;
}

.add-card-modal {
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 0.5px solid var(--color-border);
  /* 90vw with a hard cap of 1200 px to match the standalone page width.
     A taller window keeps the modal at 80vh max so the page chrome
     behind it isn't completely hidden. Flex column so the body can
     stretch and overflow internally. */
  width: 90vw;
  max-width: 1200px;
  height: 80vh;
  min-height: 480px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.22);
  overflow: hidden;
}

.add-card-modal.is-narrow {
  /* Below 800 px viewport — fullscreen modal so we don't waste edge
     padding on tiny screens. */
  width: 100vw;
  height: 100vh;
  max-width: none;
  min-height: 100vh;
  border-radius: 0;
}

/* ───────── Header ───────── */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 20px;
  border-bottom: 0.5px solid var(--color-border);
  flex-shrink: 0;
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: var(--color-text-primary);
  line-height: 1.3;
}

.section-name {
  font-weight: 600;
  color: var(--color-accent);
}

.close-btn {
  background: transparent;
  border: 0.5px solid transparent;
  border-radius: var(--radius-md);
  padding: 4px 11px;
  font-size: 22px;
  line-height: 1;
  color: var(--color-text-secondary);
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.close-btn:hover {
  background: var(--color-bg-soft);
  color: var(--color-text-primary);
  border-color: var(--color-border);
}

.close-btn:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}

/* ───────── Tab strip ───────── */
.tab-strip {
  display: flex;
  gap: 0;
  padding: 0 20px;
  border-bottom: 0.5px solid var(--color-border);
  background: var(--color-bg-soft);
  flex-shrink: 0;
}

.tab-btn {
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -0.5px;
  padding: 10px 14px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-tertiary);
  cursor: pointer;
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  transition: color 0.15s, border-color 0.15s;
}

.tab-btn:hover {
  color: var(--color-text-secondary);
}

.tab-btn.is-active {
  color: var(--color-accent);
  border-bottom-color: var(--color-accent);
  font-weight: 600;
}

.tab-count {
  font-size: 11px;
  color: var(--color-text-tertiary);
  font-weight: 500;
  font-variant-numeric: tabular-nums;
}

.tab-btn.is-active .tab-count {
  color: var(--color-accent);
}

/* ───────── Body ───────── */
.modal-body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 16px 20px;
  /* The tab panels lay out as flex columns too; their internal
     section-groups manage their own scroll, so we don't add overflow
     here. */
}

.tab-panel {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/* The embedded browser is a flex column itself with an internal scroll
   area for its section groups. We need it to fill the tab-panel so its
   section-groups child can correctly compute "available height" and
   scroll when content exceeds. Without this, the browser would size
   to natural content height and overflow the modal. */
.tab-panel :deep(.question-bank-browser) {
  flex: 1 1 auto;
  min-height: 0;
}

.placeholder-panel {
  align-items: center;
  justify-content: center;
  text-align: center;
}

.placeholder-content {
  max-width: 480px;
}

.placeholder-title {
  font-size: 17px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 10px;
}

.placeholder-text {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.5;
}

/* ───────── Flow cards list ───────── */
.flow-message {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 8px 2px;
}

.flow-error {
  color: var(--color-bad-dark);
}

.flow-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
}

.flow-row {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: var(--color-bg-card);
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  transition: background 0.15s, border-color 0.15s;
}

.flow-row:hover {
  background: var(--color-accent-soft);
  border-color: var(--color-accent);
}

.flow-row-main {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.flow-row-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-primary);
}

.flow-row-code {
  font-size: 12px;
  color: var(--color-text-tertiary);
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}

.flow-row-add {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-accent);
}

/* ───────── Toast ───────── */
.toast {
  position: fixed;
  left: 50%;
  bottom: 28px;
  transform: translateX(-50%);
  background: var(--color-text-primary);
  color: white;
  padding: 11px 18px;
  border-radius: var(--radius-md);
  font-size: 13px;
  line-height: 1.4;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.22);
  max-width: min(90vw, 560px);
  z-index: 3000;
}

.toast-info {
  background: var(--color-accent);
}

.toast-error {
  background: var(--color-bad-dark);
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, 8px);
}
</style>
